import type { IgnoreModifier } from '@apollo/client/cache'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql, maskFragments } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { client } from '~publish/legacy/apollo-client'
import { NoteType } from '~publish/legacy/notes/constants'

import { useTypedMutation } from '../../hooks/useTypedMutation'

export const UpdatedNoteOnPost = graphql(`
  fragment UpdatedNoteOnPost on Post {
    __typename
    id
    notes {
      __typename
      id
      text
      type
      createdAt
      updatedAt
      author {
        __typename
        id
        email
        avatar
        isDeleted
        name
      }
      allowedActions
    }
  }
`)

export const AddNoteToPost = graphql(
  `
    mutation AddNoteToPost($postId: PostId!, $text: String!) {
      addNoteToPost(input: { postId: $postId, text: $text }) {
        __typename
        ... on AddNoteToPostSuccess {
          __typename
          post {
            ...UpdatedNoteOnPost
          }
        }
        ... on MutationError {
          message
        }
      }
    }
  `,
  [UpdatedNoteOnPost],
)

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const useAddNoteToPost = () => {
  const { account } = useAccount()

  return useTypedMutation(AddNoteToPost, (data) => data.addNoteToPost, {
    successTypename: 'AddNoteToPostSuccess',
    optimisticResponse: (
      { postId, text },
      { IGNORE },
    ):
      | {
          addNoteToPost: {
            __typename: 'AddNoteToPostSuccess'
            post: FragmentOf<typeof UpdatedNoteOnPost>
          }
        }
      | IgnoreModifier => {
      const existingPost = client.readFragment({
        id: client.cache.identify({ __typename: 'Post', id: postId }),
        fragment: UpdatedNoteOnPost,
      })

      if (!existingPost) {
        return IGNORE
      }

      return {
        addNoteToPost: {
          __typename: 'AddNoteToPostSuccess',
          post: maskFragments([UpdatedNoteOnPost], {
            id: postId,
            __typename: 'Post',
            notes: [
              ...existingPost.notes,
              {
                __typename: 'Note',
                id: 'optimistic-note-id',
                text,
                type: NoteType.UserGenerated,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                author: {
                  __typename: 'Author',
                  id: account?.id ?? '',
                  email: account?.email ?? '',
                  avatar: account?.avatar ?? '',
                  isDeleted: false,
                  name: account?.name ?? null,
                },
                allowedActions: [],
              },
            ],
          }),
        },
      }
    },
  })
}
